import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getFirestore, doc, setDoc, updateDoc, serverTimestamp, getDoc, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { emailService } from '../services/emailService';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { currentUser } = useAuth();
  const [cartId, setCartId] = useState(() => {
    const existing = localStorage.getItem('cartId');
    console.log('[CartContext] init cartId from localStorage =', existing);
    return existing || null;
  });
  const [cart, setCart] = useState(null);

  useEffect(() => {
    console.log('[CartContext] currentUser change', currentUser?.uid);
    const load = async () => {
      if (!cartId) return;
      console.log('[CartContext] loading cart by id from localStorage', cartId);
      const ref = doc(db, 'orders', cartId);
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          if (data.userId && currentUser?.uid && data.userId !== currentUser.uid) {
            console.warn('[CartContext] cart belongs to another user, clearing');
            localStorage.removeItem('cartId');
            setCartId(null);
            setCart(null);
            return;
          }
          console.log('[CartContext] loaded cart', { items: data.items?.length || 0, status: data.status });
          setCart({ id: cartId, ...data });
        } else {
          console.warn('[CartContext] no cart found for id', cartId);
        }
      } catch (err) {
        console.error('[CartContext] getDoc failed', err);
        // If the cart doc was created while signed out, it may have userId=null and become unreadable
        // under strict rules. Try to "claim" it after login by setting userId/userEmail, then reload.
        if (err?.code === 'permission-denied' && currentUser?.uid) {
          try {
            console.warn('[CartContext] permission-denied on cart load; attempting to claim cart', cartId);
            await updateDoc(ref, { userId: currentUser.uid, userEmail: currentUser.email || null });
            const snap2 = await getDoc(ref);
            if (snap2.exists()) {
              const data2 = snap2.data();
              console.log('[CartContext] claimed + loaded cart', { items: data2.items?.length || 0, status: data2.status });
              setCart({ id: cartId, ...data2 });
              return;
            }
          } catch (claimErr) {
            console.error('[CartContext] claim cart failed', claimErr);
          }
        }
      }
    };
    load();
  }, [cartId, currentUser?.uid]);

  // If user logs in and we don't have a local cartId, try to resume their last draft order
  useEffect(() => {
    const resume = async () => {
      if (!currentUser?.uid || cartId) return;
      try {
        // Basic query (no composite index required)
        const q = query(
          collection(db, 'orders'),
          where('userId', '==', currentUser.uid),
          where('status', '==', 'cart')
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          // Pick the most recent by createdAt
          const docs = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          const latest = docs[0];
          localStorage.setItem('cartId', latest.id);
          setCartId(latest.id);
          setCart({ id: latest.id, ...latest });
          console.log('[CartContext] resumed draft cart', latest.id);
        } else {
          console.log('[CartContext] no existing draft cart for user');
        }
      } catch (err) {
        console.error('[CartContext] resume query failed', err);
      }
    };
    resume();
  }, [currentUser?.uid, cartId]);

  const addToCart = async (item) => {
    console.log('[CartContext] addToCart called with item', item, 'user', currentUser?.uid);
    const genId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `order_${Date.now()}_${Math.random().toString(16).slice(2)}`);
    const id = cartId || genId();
    console.log('[CartContext] using cart id', id);
    const ref = doc(db, 'orders', id);
    const prevItems = cart?.items || [];
    // merge by productId+options
    const keyOf = (it) => JSON.stringify({ p: it.productId, o: it.options });
    const existingIndex = prevItems.findIndex((it) => keyOf(it) === keyOf(item));
    let nextItems;
    if (existingIndex >= 0) {
      const copy = [...prevItems];
      const existing = copy[existingIndex];
      copy[existingIndex] = { ...existing, qty: (existing.qty || 1) + (item.qty || 1) };
      nextItems = copy;
    } else {
      nextItems = prevItems.concat({ ...item, qty: item.qty || 1 });
    }
    console.log('[CartContext] prevItems', prevItems.length, 'existingIndex merge?', existingIndex);
    try {
      await setDoc(ref, {
        userId: currentUser ? currentUser.uid : null,
        userEmail: currentUser ? currentUser.email : null,
        status: 'cart',
        createdAt: serverTimestamp(),
        items: nextItems,
        shipping: cart?.shipping || null,
        payment: cart?.payment || null,
      }, { merge: true });
      console.log('[CartContext] setDoc done. items now', nextItems.length);
      localStorage.setItem('cartId', id);
      console.log('[CartContext] localStorage cartId set to', localStorage.getItem('cartId'));
      setCartId(id);
      setCart((c) => {
        const next = { id, ...(c || {}), items: nextItems };
        console.log('[CartContext] state cart set', { id: next.id, items: next.items?.length });
        return next;
      });
      return id;
    } catch (err) {
      console.error('[CartContext] setDoc failed', err);
      throw err;
    }
  };

  const updateShipping = async (shipping) => {
    if (!cartId) return;
    const ref = doc(db, 'orders', cartId);
    await updateDoc(ref, { shipping });
    setCart((c) => ({ ...(c || {}), shipping }));
  };

  const updatePayment = async (payment) => {
    if (!cartId) return;
    const ref = doc(db, 'orders', cartId);
    await updateDoc(ref, { payment });
    setCart((c) => ({ ...(c || {}), payment }));
  };

  const updateItemQty = async (index, qty) => {
    if (!cartId || !cart?.items) return;
    const items = cart.items.map((it, i) => i === index ? { ...it, qty: Math.max(1, qty) } : it);
    await updateDoc(doc(db, 'orders', cartId), { items });
    setCart((c) => ({ ...(c || {}), items }));
  };

  const removeItem = async (index) => {
    if (!cartId || !cart?.items) return;
    const items = cart.items.filter((_, i) => i !== index);
    await updateDoc(doc(db, 'orders', cartId), { items });
    setCart((c) => ({ ...(c || {}), items }));
  };

  const getSubtotal = () => (cart?.items || []).reduce((sum, it) => sum + (it.price || 0) * (it.qty || 1), 0);

  const finalizeOrder = async (shippingDetails = null) => {
    if (!cartId) return null;
    try {
      await updateDoc(doc(db, 'orders', cartId), { status: 'processing', updatedAt: serverTimestamp() });
      console.log('[CartContext] order finalized to processing', cartId);
      
      // Send processing email notification
      const emailData = shippingDetails || cart?.shipping;
      if (emailData?.contact?.email && cart?.items) {
        try {
          const customerName = `${emailData.firstName || ''} ${emailData.lastName || ''}`.trim() || 'Valued Customer';
          const totalAmount = cart.items.reduce((sum, item) => sum + ((item.price || 0) * (item.qty || 1)), 0);
          
          await emailService.sendOrderProcessingEmail(
            emailData.contact.email,
            cartId,
            customerName,
            cart.items,
            totalAmount
          );
          console.log('[CartContext] Processing email sent for order:', cartId);
        } catch (emailError) {
          console.error('[CartContext] Email notification failed:', emailError);
          // Don't fail the order finalization if email fails
        }
      } else {
        console.log('[CartContext] No email sent - missing data:', {
          hasEmail: !!emailData?.contact?.email,
          hasItems: !!cart?.items,
          shippingDetails: !!shippingDetails,
          cartShipping: !!cart?.shipping
        });
      }
      
      return cartId;
    } catch (err) {
      console.error('[CartContext] finalizeOrder failed', err);
      throw err;
    }
  };

  const clearCart = (keepServerDraft = true) => {
    // Keep Firestore draft; only clear local linkage unless explicitly asked
    localStorage.removeItem('cartId');
    setCartId(null);
    setCart(null);
  };

  const value = useMemo(() => ({ cartId, cart, addToCart, updateShipping, updatePayment, updateItemQty, removeItem, getSubtotal, finalizeOrder, clearCart }), [cartId, cart]);

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);


